/**
 * Contact form — wires inputs, mask, validation, submit, toast feedback.
 * Uses the existing apiPost helper to POST /api/contact.
 */
import { apiPost, ApiError } from '../api'
import { createIceButton } from '../ui/iceButton'
import { showToast } from '../ui/toast'

interface ContactPayload {
  name: string
  phone: string
  message: string
  website: string
}

interface ContactResponse {
  ok: boolean
  id?: string
  stored?: boolean
}

function maskPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 2) return d
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

function clearError(id: string) {
  const el = document.getElementById(id)
  if (el) el.textContent = ''
}

function setError(id: string, msg: string) {
  const el = document.getElementById(id)
  if (el) el.textContent = msg
}

export function mountContact(): void {
  const form = document.getElementById('contact-form') as HTMLFormElement | null
  const nameInput = document.getElementById('contact-name') as HTMLInputElement | null
  const phoneInput = document.getElementById('contact-phone') as HTMLInputElement | null
  const msgInput = document.getElementById('contact-message') as HTMLTextAreaElement | null
  const websiteInput = document.getElementById('contact-website') as HTMLInputElement | null
  const counter = document.getElementById('contact-counter') as HTMLSpanElement | null
  const submitRow = document.getElementById('contact-submit-row') as HTMLDivElement | null

  if (!form || !nameInput || !phoneInput || !msgInput || !websiteInput || !counter || !submitRow) {
    return
  }

  // Rebind to non-null locals so TypeScript narrowing survives inside closures
  const formEl: HTMLFormElement = form
  const nameEl: HTMLInputElement = nameInput
  const phoneEl: HTMLInputElement = phoneInput
  const msgEl: HTMLTextAreaElement = msgInput
  const websiteEl: HTMLInputElement = websiteInput
  const counterEl: HTMLSpanElement = counter

  // Phone mask
  phoneEl.addEventListener('input', () => {
    phoneEl.value = maskPhone(phoneEl.value)
    try {
      phoneEl.setSelectionRange(phoneEl.value.length, phoneEl.value.length)
    } catch {
      /* ignore */
    }
    clearError('contact-phone-error')
  })

  // Live counter
  msgEl.addEventListener('input', () => {
    counterEl.textContent = `${msgEl.value.length} / 1000`
    clearError('contact-message-error')
  })

  nameEl.addEventListener('input', () => clearError('contact-name-error'))

  // Submit button (ice button)
  submitRow.innerHTML = ''
  const submitBtn = createIceButton({
    label: 'Enviar mensagem',
    variant: 'primary',
    size: 'large',
    onClick: handleSubmit,
  })
  submitRow.appendChild(submitBtn)

  async function handleSubmit(e: MouseEvent) {
    e.preventDefault()

    let hasError = false

    clearError('contact-name-error')
    clearError('contact-phone-error')
    clearError('contact-message-error')

    if (nameEl.value.trim().length < 2) {
      setError('contact-name-error', 'Informe seu nome')
      hasError = true
    }
    if (phoneEl.value.replace(/\D/g, '').length < 10) {
      setError('contact-phone-error', 'Telefone inválido')
      hasError = true
    }
    if (msgEl.value.trim().length < 5) {
      setError('contact-message-error', 'Mensagem muito curta')
      hasError = true
    }

    if (hasError) return

    const btn = submitBtn as HTMLButtonElement
    const originalLabel = btn.querySelector('.ice-button__label')?.textContent ?? 'Enviar'
    btn.setAttribute('aria-busy', 'true')
    btn.setAttribute('disabled', 'true')
    const labelEl = btn.querySelector('.ice-button__label')
    if (labelEl) labelEl.textContent = 'Enviando...'

    const payload: ContactPayload = {
      name: nameEl.value.trim(),
      phone: phoneEl.value.trim(),
      message: msgEl.value.trim(),
      website: websiteEl.value,
    }

    try {
      const res = await apiPost<ContactResponse>('/api/contact', payload)
      if (res.ok) {
        showToast(
          res.stored === false
            ? 'Mensagem recebida! (Firestore não configurado — veja logs)'
            : 'Mensagem enviada! Em breve entraremos em contato.',
          'ok',
          5000,
        )
        formEl.reset()
        counterEl.textContent = '0 / 1000'
      } else {
        showToast('Não foi possível enviar. Tente novamente.', 'error')
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Erro desconhecido'
      showToast(`Erro: ${msg}`, 'error')
    } finally {
      btn.removeAttribute('aria-busy')
      btn.removeAttribute('disabled')
      if (labelEl) labelEl.textContent = originalLabel
    }
  }

  // Prevent native form submit (we handle via button click)
  formEl.addEventListener('submit', (e) => {
    e.preventDefault()
    ;(submitBtn as HTMLButtonElement).click()
  })
}
